import { Switch, type SwitchProps } from 'antd';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';

export type ISwitchProps = Omit<SwitchProps, 'value'> & {
  label?: string;
  error?: string;
  hideErrorMessage?: boolean;
  isRequired?: boolean;
  onChange?: (checked: boolean) => void;
  classes?: {
    root?: string;
    label?: string;
    error?: string;
  };
  isFrozen?: boolean;
};

const FormFieldSwitch: React.FC<ISwitchProps> = forwardRef<
  HTMLInputElement,
  ISwitchProps
>(
  (
    { label, error, hideErrorMessage, isRequired, classes, isFrozen, ...rest },
    forwardedRef
  ) => {
    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    const errorClassCondition = hideErrorMessage ? error !== undefined : error;

    const rootClass = ['form-field', classes?.root ? classes?.root : ''].join(
      ' '
    );
    const wrapperClass = [
      'form-field__wrapper',
      errorClassCondition ? 'form-field__wrapper--error' : ''
    ].join(' ');
    const labelClass = [
      'form-field__label',
      classes?.label ? classes.label : ''
    ].join(' ');
    const checkboxClass = [
      'form-field__field',
      rest.className ? rest.className : ''
    ].join(' ');
    const errorClass = classes?.error ? classes.error : '';

    return (
      <div className={rootClass}>
        <label className={wrapperClass}>
          <Switch
            {...rest}
            className={checkboxClass}
            checked={rest.checked}
            onChange={(checked) => {
              if (isFrozen) return;
              rest.onChange?.(checked);
            }}
            disabled={isFrozen}
          />
          {label && (
            <span className={labelClass}>
              {label}
              {isRequired && <span className="form-field__require">*</span>}
            </span>
          )}
        </label>
        {error && !hideErrorMessage && (
          <p
            className={`form-field__error ${errorClass}`}
            onClick={() => ref.current?.focus()}
          >
            <AlertCircleIcon />
            {error}
          </p>
        )}
      </div>
    );
  }
);

FormFieldSwitch.displayName = 'FormFieldSwitch';

export default FormFieldSwitch;
